import {
  CalendarOff,
  CalendarPlus,
  GraduationCap,
  Pencil,
  Play,
  Plus,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { DeleteDialog } from "@/components/delete-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import { ApiError, adminApi, type Course, type Term } from "@/lib/api";
import { adminCourseStore } from "@/lib/auth";

/**
 * Season options. `label` is capitalised for display and `month` fixes the term's
 * `starts_at`, which is what orders terms -- the id cannot, since "2026-fall" sorts
 * before "2026-spring" alphabetically but follows it in time.
 *
 * The labels also feed `items` on the Select, because Base UI's trigger shows the
 * raw value otherwise. Here the value happens to read acceptably, so this one was
 * not visibly broken -- which is exactly why it is worth making explicit.
 */
const SEASONS = [
  { value: "spring", label: "Spring", month: 0 },
  { value: "summer", label: "Summer", month: 5 },
  { value: "fall", label: "Fall", month: 8 },
  { value: "winter", label: "Winter", month: 11 },
] as const;

const SEASON_LABELS = Object.fromEntries(SEASONS.map((s) => [s.value, s.label]));

function seasonLabel(termId: string): string {
  const [year, season] = termId.split("-");
  if (!year || !season) return termId;
  return `${season[0].toUpperCase()}${season.slice(1)} ${year}`;
}

/**
 * Terms and courses.
 *
 * Deliberately *not* scoped by the course selector: these are the records that
 * selector chooses between. Mixing them into the same navigation level as
 * Challenges, Students and Teams — which all act within one course — was what made
 * the console confusing.
 *
 * Retiring a term is the main reason this page exists. It is the end-of-semester
 * action, and until now it had no interface at all: a TA had to know the API. The
 * consequence of never doing it is that a finished cohort keeps working credentials
 * indefinitely.
 */
export function AdminTermsRoute() {
  const adminKey = useAdminKey();
  const [newTerm, setNewTerm] = useState(false);
  const [newCourseFor, setNewCourseFor] = useState<Term | null>(null);
  const [editing, setEditing] = useState<Course | null>(null);
  const [retiring, setRetiring] = useState<Term | null>(null);
  const [deleting, setDeleting] = useState<{ kind: "term"; term: Term } | { kind: "course"; course: Course } | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  const terms = useAsync(
    adminKey ? () => adminApi.terms(adminKey) : null,
    [adminKey, nonce],
  );
  const courses = useAsync(
    adminKey ? () => adminApi.courses(adminKey) : null,
    [adminKey, nonce],
  );

  // Two unscoped requests rather than one per course: at this size counting
  // client-side is cheaper than a round trip each, and there is no counts endpoint.
  const keys = useAsync(adminKey ? () => adminApi.keys(adminKey) : null, [
    adminKey,
    nonce,
  ]);
  const challenges = useAsync(
    adminKey ? () => adminApi.challenges(adminKey) : null,
    [adminKey, nonce],
  );

  const studentCount = new Map<string, number>();
  for (const key of keys.data ?? [])
    studentCount.set(key.course_id, (studentCount.get(key.course_id) ?? 0) + 1);
  const challengeCount = new Map<string, number>();
  for (const c of challenges.data ?? [])
    challengeCount.set(c.course_id, (challengeCount.get(c.course_id) ?? 0) + 1);

  const byTerm = new Map<string, Course[]>();
  for (const course of courses.data ?? []) {
    byTerm.set(course.term_id, [...(byTerm.get(course.term_id) ?? []), course]);
  }

  async function setTermActive(term: Term, active: boolean) {
    if (!adminKey) return;
    try {
      await adminApi.patchTerm(term.id, { active }, adminKey);
      toast.success(active ? `${term.label} reopened` : `${term.label} retired`);
      reload();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground max-w-2xl text-sm">
          A term offers courses; a course owns its own challenges, students and
          teams. Retiring a term invalidates every API key beneath it in one step,
          while leaving its leaderboards readable.
        </p>
        <Button onClick={() => setNewTerm(true)}>
          <CalendarPlus className="size-4" aria-hidden />
          New term
        </Button>
      </div>

      <AsyncBoundary
        state={terms}
        empty={
          <EmptyState
            title="No terms yet"
            description="Create one to start a semester."
          />
        }
      >
        {(list) => (
          <div className="space-y-4">
            {list.map((term) => {
              const termCourses = byTerm.get(term.id) ?? [];
              return (
                <Card key={term.id}>
                  <CardHeader className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <CardTitle className="text-base">{term.label}</CardTitle>
                      <code className="text-muted-foreground text-xs">{term.id}</code>
                      {term.active ? (
                        <Badge>Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <CalendarOff className="size-3" aria-hidden />
                          Retired
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setNewCourseFor(term)}
                      >
                        <Plus className="size-4" aria-hidden />
                        Course
                      </Button>
                      {term.active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRetiring(term)}
                        >
                          <CalendarOff className="size-4" aria-hidden />
                          Retire
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setTermActive(term, true)}
                        >
                          <Play className="size-4" aria-hidden />
                          Reopen
                        </Button>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent>
                    {termCourses.length ? (
                      <ul className="divide-y rounded-md border">
                        {termCourses.map((course) => (
                          <li
                            key={course.id}
                            className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5"
                          >
                            <GraduationCap
                              className="text-muted-foreground size-4 shrink-0"
                              aria-hidden
                            />
                            <span className="font-medium">{course.code}</span>
                            {course.title ? (
                              <span className="text-muted-foreground text-sm">
                                {course.title}
                              </span>
                            ) : null}
                            {!course.active ? (
                              <Badge variant="secondary">Closed</Badge>
                            ) : null}

                            <span className="text-muted-foreground ml-auto flex items-center gap-4 text-sm tabular-nums">
                              <span>{studentCount.get(course.id) ?? 0} students</span>
                              <span>
                                {challengeCount.get(course.id) ?? 0} challenges
                              </span>
                            </span>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                adminCourseStore.set(course.id);
                                setEditing(course);
                              }}
                            >
                              <Pencil className="size-4" aria-hidden />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleting({ kind: "course", course })}
                              title="Delete the course and everything in it"
                            >
                              <Trash2 className="size-4" aria-hidden />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground rounded-md border border-dashed px-3 py-6 text-center text-sm">
                        No courses in this term yet.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </AsyncBoundary>

      <NewTermDialog
        key={`term-${newTerm}`}
        open={newTerm}
        onOpenChange={setNewTerm}
        existing={terms.data ?? []}
        onDone={reload}
      />
      <NewCourseDialog
        key={`course-${newCourseFor?.id ?? "none"}`}
        term={newCourseFor}
        onOpenChange={(open) => !open && setNewCourseFor(null)}
        onDone={(course) => {
          adminCourseStore.set(course.id);
          reload();
        }}
      />
      <EditCourseDialog
        key={`edit-${editing?.id ?? "none"}`}
        course={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onDone={reload}
      />
      <DeleteTermOrCourseDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />
      <RetireTermDialog
        term={retiring}
        studentCount={
          (byTerm.get(retiring?.id ?? "") ?? []).reduce(
            (sum, c) => sum + (studentCount.get(c.id) ?? 0),
            0,
          )
        }
        onOpenChange={(open) => !open && setRetiring(null)}
        onConfirm={(term) => {
          setRetiring(null);
          void setTermActive(term, false);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Deleting a term or a course is for undoing a mistake -- a duplicate, a cohort synced
 * into the wrong place. Retiring is for ending one. The dialog says which is which and
 * shows the server's count of what would go.
 */
function DeleteTermOrCourseDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: { kind: "term"; term: Term } | { kind: "course"; course: Course } | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const adminKey = useAdminKey();
  const id = target?.kind === "term" ? target.term.id : target?.kind === "course" ? target.course.id : null;
  const kind = target?.kind ?? null;
  const preview = useAsync(
    id && kind && adminKey
      ? () => (kind === "term" ? adminApi.previewDeleteTerm(id, adminKey) : adminApi.previewDeleteCourse(id, adminKey))
      : null,
    [id, kind, adminKey],
  );
  const name = target?.kind === "term" ? target.term.label : target?.kind === "course" ? target.course.code : "";

  return (
    <DeleteDialog
      open={target !== null}
      onOpenChange={(next) => !next && onClose()}
      title={`Delete ${kind ?? ""} ${name}?`}
      description={preview.data}
      loadingDescription={preview.loading}
      confirmToken={id ?? ""}
      onConfirm={async () => {
        if (!adminKey || !id || !kind) return;
        if (kind === "term") await adminApi.deleteTerm(id, adminKey);
        else await adminApi.deleteCourse(id, adminKey);
        if (kind === "course" && adminCourseStore.get() === id) adminCourseStore.clear();
        toast.success(`Deleted ${name}`);
      }}
      onDone={onDeleted}
    >
      <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
        To end a {kind ?? "term"} without losing anything, {kind === "course" ? "close it with Edit" : "Retire it"} instead:
        keys stop working, leaderboards stay readable.
      </p>
    </DeleteDialog>
  );
}

function NewTermDialog({
  open,
  onOpenChange,
  existing,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: Term[];
  onDone: () => void;
}) {
  const adminKey = useAdminKey();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [season, setSeason] = useState<string>("spring");
  const [busy, setBusy] = useState(false);

  const id = `${year}-${season}`;
  const clash = existing.some((t) => t.id === id);

  async function submit() {
    if (!adminKey) return;
    setBusy(true);
    try {
      await adminApi.createTerm(
        {
          id,
          label: seasonLabel(id),
          starts_at: new Date(
            Number(year),
            SEASONS.find((s) => s.value === season)?.month ?? 0,
            1,
          ).getTime(),
        },
        adminKey,
      );
      toast.success(`Created ${seasonLabel(id)}`);
      onDone();
      onOpenChange(false);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>New term</DialogTitle>
          <DialogDescription>An academic semester.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <div className="space-y-2">
            <Label htmlFor="t-year">Year</Label>
            <Input
              id="t-year"
              className="w-28"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label htmlFor="t-season">Season</Label>
            <Select
              items={SEASON_LABELS}
              value={season}
              onValueChange={(v) => setSeason(v ?? "spring")}
            >
              <SelectTrigger id="t-season" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEASONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-muted-foreground text-xs">
          Identifier: <code>{id}</code>
          {clash ? " — already exists" : null}
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || clash || !/^\d{4}$/.test(year)}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewCourseDialog({
  term,
  onOpenChange,
  onDone,
}: {
  term: Term | null;
  onOpenChange: (open: boolean) => void;
  onDone: (course: Course) => void;
}) {
  const adminKey = useAdminKey();
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!adminKey || !term) return;
    setBusy(true);
    try {
      const course = await adminApi.createCourse(
        { term_id: term.id, code: code.trim(), title: title.trim() },
        adminKey,
      );
      toast.success(`Created ${course.code}`);
      onDone(course);
      onOpenChange(false);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={term !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New course in {term?.label}</DialogTitle>
          <DialogDescription>
            Courses are independent: each has its own challenges, students and
            teams. Cross-listed sections that share a leaderboard should be one
            course, not two.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="c-code">Course code</Label>
            <Input
              id="c-code"
              placeholder="ROB-UY 3203"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="c-title">Title</Label>
            <Input
              id="c-title"
              placeholder="Robot Perception"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !code.trim()}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCourseDialog({
  course,
  onOpenChange,
  onDone,
}: {
  course: Course | null;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const adminKey = useAdminKey();
  const [code, setCode] = useState(course?.code ?? "");
  const [title, setTitle] = useState(course?.title ?? "");
  const [active, setActive] = useState(course?.active ?? true);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!adminKey || !course) return;
    setBusy(true);
    try {
      await adminApi.patchCourse(
        course.id,
        { code: code.trim(), title: title.trim(), active },
        adminKey,
      );
      toast.success("Course updated");
      onDone();
      onOpenChange(false);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={course !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit course</DialogTitle>
          <DialogDescription>
            The identifier <code>{course?.id}</code> does not change, because stored
            challenges and enrollments refer to it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ec-code">Course code</Label>
            <Input
              id="ec-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ec-title">Title</Label>
            <Input
              id="ec-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
            <Checkbox
              className="mt-0.5"
              checked={!active}
              onCheckedChange={(next) => setActive(next !== true)}
            />
            <span>
              <span className="text-sm font-medium">Closed</span>
              <span className="text-muted-foreground block text-xs">
                Invalidates this course's API keys, without affecting the rest of
                the term. Useful when one section finishes early.
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !code.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RetireTermDialog({
  term,
  studentCount,
  onOpenChange,
  onConfirm,
}: {
  term: Term | null;
  studentCount: number;
  onOpenChange: (open: boolean) => void;
  onConfirm: (term: Term) => void;
}) {
  return (
    <Dialog open={term !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Retire {term?.label}?</DialogTitle>
          <DialogDescription>
            This is the normal way to end a semester. It is reversible.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 text-sm">
          <li className="flex gap-2">
            <span aria-hidden>•</span>
            <span>
              All{" "}
              <strong className="tabular-nums">{studentCount}</strong> API key(s) in
              this term stop working. Students get a message naming the term rather
              than a bare rejection.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden>•</span>
            <span>
              Leaderboards, submissions and recordings stay exactly as they are, and
              stay publicly readable — grade appeals outlive the semester.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden>•</span>
            <span>Nothing is deleted. Reopen the term to undo.</span>
          </li>
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => term && onConfirm(term)}>Retire term</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
